import React, { useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import AiTextEditor from 'components/AI/AiTextEditor';
import { EAIEditorType } from 'types/ai';
import { remindPracticeProvider } from 'api/textVisit';
import s from './remindModal.module.less';

interface RemindProviderModalProps {
    open: boolean;
    providerId?: number;
    providerName: string;
    textVisitId?: number;
    patientName?: string;
    waitingDays?: number;
    onClose: () => void;
}

const buildDefaultMessage = (patientName?: string, waitingDays?: number) => {
    const who = patientName?.trim() || 'a patient';
    const when =
        waitingDays && waitingDays > 0
            ? ` — it was submitted ${waitingDays} ${waitingDays === 1 ? 'day' : 'days'} ago`
            : '';
    return `You have a pending text visit request from ${who} that hasn't been completed yet${when}. Please review and respond at your earliest convenience.

Thank you.`;
};

const RemindProviderModal: React.FC<RemindProviderModalProps> = ({
    open,
    providerId,
    providerName,
    textVisitId,
    patientName,
    waitingDays,
    onClose,
}) => {
    const [content, setContent] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open) {
            setContent(buildDefaultMessage(patientName, waitingDays));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSend = async () => {
        if (!providerId || !content.trim()) {
            return;
        }
        try {
            setSending(true);
            const res = await remindPracticeProvider(providerId, {
                message: content.trim(),
                textVisitId,
            });
            if (!res.error) {
                message.success('Reminder sent successfully');
                onClose();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to send reminder:', e);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className={`${s.newMsgWrap} ${open ? s.newMsgWrapShow : ''}`}>
            <div className={s.nTitle}>
                <span>New message</span>
                <svg
                    onClick={onClose}
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                >
                    <path
                        d="M6 18L18 6M6 6L18 18"
                        stroke="#4B5563"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
            <div className={s.nContent}>
                <div className={s.nLabel}>To (Provider)</div>
                <div className={s.providerBox}>
                    <span>{providerName || '—'}</span>
                    <DownOutlined className={s.chevron} />
                </div>
                <div className={s.nLabel}>Message</div>
                <AiTextEditor
                    type={EAIEditorType.TEXT_VISIT_REMINDER_MESSAGE}
                    value={content}
                    onChange={(val) => setContent(val as string)}
                    rows={6}
                    maxLength={1000}
                    hideWriteLike
                    relationId={textVisitId}
                    placeholder="Type your message…"
                />
            </div>
            <div className={s.nFooter}>
                <Button style={{ marginRight: '16px' }} onClick={onClose}>
                    Discard
                </Button>
                <Button
                    loading={sending}
                    disabled={!content.trim()}
                    type="primary"
                    onClick={handleSend}
                >
                    Send
                </Button>
            </div>
        </div>
    );
};

export default RemindProviderModal;
